import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { Mail, MessageCircle } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/contact", form);
      toast.success(res.data.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16" data-testid="contact-page">
      <Seo
        title="Contact"
        description="Have a question about a book, resource, or want to say hello? Reach out to Laura Combs Productions — we read every message with care and reply within 2 business days."
        path="/contact"
      />
      <div>
        <span className="text-gold text-sm uppercase tracking-[0.3em]">Let's talk</span>
        <h1 className="font-serif text-5xl sm:text-6xl text-forest mt-4 leading-tight">
          Reach out — we'd love to hear from you.
        </h1>
        <p className="text-charcoal/70 mt-6 leading-relaxed">
          Whether you have a question about a book, a course, or simply want to say hello,
          Laura's team reads every message with care.
        </p>
        <div className="mt-10 space-y-5">
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full bg-white border border-gold/40 flex items-center justify-center">
              <Mail className="w-5 h-5 text-gold" />
            </span>
            <span className="text-charcoal/70">hello@lauracombsproductions.com</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full bg-white border border-gold/40 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-gold" />
            </span>
            <span className="text-charcoal/70">We typically respond within 2 business days.</span>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white rounded-2xl border border-gold/30 shadow-sm p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="c-name" className="text-charcoal">Name</Label>
            <Input id="c-name" value={form.name} onChange={set("name")} required
              className="mt-2 rounded-xl border-gold/40 focus-visible:ring-gold" data-testid="contact-name" />
          </div>
          <div>
            <Label htmlFor="c-email" className="text-charcoal">Email</Label>
            <Input id="c-email" type="email" value={form.email} onChange={set("email")} required
              className="mt-2 rounded-xl border-gold/40 focus-visible:ring-gold" data-testid="contact-email" />
          </div>
        </div>
        <div>
          <Label htmlFor="c-subject" className="text-charcoal">Subject</Label>
          <Input id="c-subject" value={form.subject} onChange={set("subject")} required
            className="mt-2 rounded-xl border-gold/40 focus-visible:ring-gold" data-testid="contact-subject" />
        </div>
        <div>
          <Label htmlFor="c-message" className="text-charcoal">Message</Label>
          <Textarea id="c-message" value={form.message} onChange={set("message")} required rows={5}
            className="mt-2 rounded-xl border-gold/40 focus-visible:ring-gold" data-testid="contact-message" />
        </div>
        <Button type="submit" disabled={loading}
          className="w-full rounded-full bg-forest hover:bg-forest-light text-ivory h-12 transition-colors" data-testid="contact-submit">
          {loading ? "Sending…" : "Send Message"}
        </Button>
      </form>
    </div>
  );
}
